import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useNotifStore } from '@/stores/notifications';
import { useHiStore } from '@/stores/hi';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';

// ── Helpers ─────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z')).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// ── Messages Page ───────────────────────────────────────────

interface ConversationItem {
  id: string;
  members: { id: string; display_name: string; username: string; avatar_key: string | null }[];
  last_message_content: string | null;
  last_message_at: string | null;
  unread_count: number;
}

interface MessageItem {
  id: string;
  sender_id: string;
  sender_display_name: string;
  sender_avatar_key: string | null;
  content: string;
  created_at: string;
}

export function MessagesPage() {
  const { user } = useAuthStore();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeConvo, setActiveConvo] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get<ConversationItem[]>('/conversations')
      .then(setConversations)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openConversation = async (convoId: string) => {
    setActiveConvo(convoId);
    setLoadingMsgs(true);
    try {
      const msgs = await api.get<MessageItem[]>(`/conversations/${convoId}/messages?limit=50`);
      setMessages(msgs);
    } catch {}
    setLoadingMsgs(false);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConvo || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    try {
      const msg = await api.post<MessageItem>(`/conversations/${activeConvo}/messages`, { content });
      setMessages((prev) => [...prev, msg]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvo ? { ...c, last_message_content: content, last_message_at: new Date().toISOString() } : c
        )
      );
    } catch {
      setNewMessage(content);
    }
    setSending(false);
  };

  const activeConversation = conversations.find((c) => c.id === activeConvo);

  return (
    <div className="max-w-5xl mx-auto px-3 py-4">
      <div className="card overflow-hidden" style={{ minHeight: '500px' }}>
        <div className="flex h-full" style={{ minHeight: '500px' }}>
          {/* Conversation list */}
          <div className="w-72 border-r border-sand-300 flex flex-col shrink-0">
            <div className="card-header">Messages</div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="text-xs text-sand-500 p-3 text-center">Loading...</p>
              ) : conversations.length === 0 ? (
                <p className="text-xs text-sand-500 p-4 text-center">No conversations yet. Chat with someone from their profile or the chat bar below.</p>
              ) : (
                conversations.map((convo) => {
                  const other = convo.members[0];
                  if (!other) return null;
                  return (
                    <button
                      key={convo.id}
                      onClick={() => openConversation(convo.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-sand-50 transition-colors border-b border-sand-100 ${
                        activeConvo === convo.id ? 'bg-vibe-50' : ''
                      }`}
                    >
                      <img src={mediaUrl(other.avatar_key)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs truncate ${convo.unread_count > 0 ? 'font-bold text-sand-800' : 'font-semibold text-sand-700'}`}>
                            {other.display_name}
                          </span>
                          {convo.last_message_at && (
                            <span className="text-[10px] text-sand-400 shrink-0 ml-1">{timeAgo(convo.last_message_at)}</span>
                          )}
                        </div>
                        {convo.last_message_content && (
                          <p className={`text-[11px] truncate mt-0.5 ${convo.unread_count > 0 ? 'text-sand-700 font-medium' : 'text-sand-500'}`}>
                            {convo.last_message_content}
                          </p>
                        )}
                      </div>
                      {convo.unread_count > 0 && (
                        <span className="bg-vibe-500 text-white text-[9px] font-bold rounded-full px-1.5 min-w-[16px] text-center">
                          {convo.unread_count}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Message thread */}
          <div className="flex-1 flex flex-col min-w-0">
            {!activeConvo ? (
              <div className="flex-1 flex items-center justify-center text-sand-400 text-sm">
                Select a conversation
              </div>
            ) : (
              <>
                {/* Thread header */}
                {activeConversation && activeConversation.members[0] && (
                  <div className="card-header flex items-center gap-2">
                    <img src={mediaUrl(activeConversation.members[0].avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
                    <Link to={`/profile/${activeConversation.members[0].username}`} className="link text-xs font-bold">
                      {activeConversation.members[0].display_name}
                    </Link>
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand-50">
                  {loadingMsgs ? (
                    <p className="text-center text-xs text-sand-500 py-4">Loading messages...</p>
                  ) : messages.length === 0 ? (
                    <p className="text-center text-xs text-sand-400 py-8">No messages yet. Say something!</p>
                  ) : (
                    messages.map((msg) => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id} className={`flex gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                          {!isMe && (
                            <img src={mediaUrl(msg.sender_avatar_key)} alt="" className="w-7 h-7 rounded object-cover shrink-0 mt-1" />
                          )}
                          <div className={`max-w-[70%] ${isMe ? 'text-right' : ''}`}>
                            <div
                              className={`inline-block px-3 py-2 rounded-lg text-xs ${
                                isMe
                                  ? 'bg-vibe-500 text-white'
                                  : 'bg-white border border-sand-300 text-sand-800'
                              }`}
                            >
                              {msg.content}
                            </div>
                            <div className="text-[10px] text-sand-400 mt-0.5">{timeAgo(msg.created_at)}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Input */}
                <div className="border-t border-sand-300 p-3 flex gap-2">
                  <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Write a message..."
                    className="input flex-1 text-xs"
                  />
                  <button onClick={handleSend} disabled={sending || !newMessage.trim()} className="btn-primary text-xs px-4 disabled:opacity-40">
                    Send
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Events Page ─────────────────────────────────────────────

interface EventItem {
  id: string;
  title: string;
  description: string;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  creator_display_name: string;
  creator_username: string;
  creator_avatar_key: string | null;
  going_count: number;
  interested_count: number;
  my_rsvp: string | null;
}

export function EventsPage() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', location: '', starts_at: '', ends_at: '' });
  const [creating, setCreating] = useState(false);

  const fetchEvents = () => {
    api.get<EventItem[]>('/events?limit=50')
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.starts_at) return;
    setCreating(true);
    try {
      await api.post('/events', {
        title: form.title.trim(),
        description: form.description.trim(),
        location: form.location.trim() || undefined,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
      });
      setForm({ title: '', description: '', location: '', starts_at: '', ends_at: '' });
      setShowForm(false);
      fetchEvents();
    } catch {}
    setCreating(false);
  };

  const handleRsvp = async (eventId: string, status: string) => {
    try {
      await api.post(`/events/${eventId}/rsvp`, { status });
      setEvents((prev) =>
        prev.map((e) => {
          if (e.id !== eventId) return e;
          const wasGoing = e.my_rsvp === 'going';
          const wasInterested = e.my_rsvp === 'interested';
          return {
            ...e,
            my_rsvp: status,
            going_count: e.going_count + (status === 'going' ? 1 : 0) - (wasGoing ? 1 : 0),
            interested_count: e.interested_count + (status === 'interested' ? 1 : 0) - (wasInterested ? 1 : 0),
          };
        })
      );
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-3 py-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-sand-800">Events</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-xs px-3 py-1.5">
          + Create Event
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card p-4 space-y-3">
          <div className="card-header -mx-4 -mt-4 mb-3">Create an Event</div>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Event name"
            className="input w-full text-sm"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What's this about?"
            rows={3}
            className="input w-full text-sm resize-none"
          />
          <input
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Location (optional)"
            className="input w-full text-sm"
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[10px] font-bold text-sand-500 uppercase">Starts</label>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                className="input w-full text-sm mt-0.5"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-bold text-sand-500 uppercase">Ends (optional)</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                className="input w-full text-sm mt-0.5"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
            <button onClick={handleCreate} disabled={creating || !form.title.trim() || !form.starts_at} className="btn-primary text-xs px-4 py-1.5 disabled:opacity-40">
              {creating ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </div>
      )}

      {/* Events list */}
      {loading ? (
        <div className="text-center py-8 text-sand-500">
          <div className="animate-pulse text-3xl mb-2">〰️</div>
          <p className="text-sm">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="card p-8 text-center text-sand-500">
          <div className="text-3xl mb-2">📅</div>
          <p className="text-sm">No upcoming events yet.</p>
          <p className="text-xs mt-1">Create one and get the neighborhood together!</p>
        </div>
      ) : (
        events.map((event) => (
          <div key={event.id} className="card overflow-hidden">
            <div className="card-header flex items-center gap-2">
              <span>📅</span>
              <span className="flex-1 truncate">{formatDate(event.starts_at)}</span>
              {event.location && <span className="text-sand-500 text-[10px]">📍 {event.location}</span>}
            </div>
            <div className="p-4">
              <h2 className="font-bold text-sand-800 text-sm mb-1">{event.title}</h2>
              {event.description && <p className="text-xs text-sand-600 mb-3">{event.description}</p>}

              <div className="flex items-center gap-3 mb-3">
                <Link to={`/profile/${event.creator_username}`} className="flex items-center gap-1.5">
                  <img src={mediaUrl(event.creator_avatar_key)} alt="" className="w-5 h-5 rounded object-cover" />
                  <span className="text-[11px] text-sand-600">Hosted by <span className="font-bold text-vibe-500">{event.creator_display_name}</span></span>
                </Link>
              </div>

              <div className="flex items-center gap-4 text-xs text-sand-500 mb-3">
                <span><span className="font-bold text-sand-700">{event.going_count}</span> going</span>
                <span><span className="font-bold text-sand-700">{event.interested_count}</span> interested</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleRsvp(event.id, 'going')}
                  className={`text-xs font-bold py-1.5 px-4 rounded-md transition-colors ${
                    event.my_rsvp === 'going' ? 'bg-vibe-500 text-white' : 'btn-secondary'
                  }`}
                >
                  {event.my_rsvp === 'going' ? '✓ Going' : 'Going'}
                </button>
                <button
                  onClick={() => handleRsvp(event.id, 'interested')}
                  className={`text-xs font-bold py-1.5 px-4 rounded-md transition-colors ${
                    event.my_rsvp === 'interested' ? 'bg-amber-100 text-amber-700' : 'btn-secondary'
                  }`}
                >
                  {event.my_rsvp === 'interested' ? '★ Interested' : 'Interested'}
                </button>
                {event.my_rsvp && event.my_rsvp !== 'declined' && (
                  <button
                    onClick={() => handleRsvp(event.id, 'declined')}
                    className="text-xs text-sand-400 hover:text-sand-600 py-1.5 px-2"
                  >
                    Can't go
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── Directory Page ──────────────────────────────────────────

interface BusinessItem {
  id: string;
  name: string;
  category: string;
  description: string;
  address: string | null;
  neighborhood: string | null;
  phone: string | null;
  website: string | null;
  avg_rating: number | null;
  review_count: number;
}

const CATEGORIES = ['restaurant', 'shop', 'service', 'entertainment', 'cafe', 'bar', 'fitness', 'other'];

export function DirectoryPage() {
  const [businesses, setBusinesses] = useState<BusinessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'restaurant', description: '', address: '', neighborhood: '', phone: '', website: '' });
  const [creating, setCreating] = useState(false);

  const fetchBusinesses = (q?: string, cat?: string) => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    if (q) params.set('q', q);
    if (cat) params.set('category', cat);
    api.get<BusinessItem[]>(`/businesses?${params}`)
      .then(setBusinesses)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBusinesses(); }, []);

  const handleSearch = () => fetchBusinesses(search, category);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.category) return;
    setCreating(true);
    try {
      await api.post('/businesses', {
        name: form.name.trim(),
        category: form.category,
        description: form.description.trim() || undefined,
        address: form.address.trim() || undefined,
        neighborhood: form.neighborhood.trim() || undefined,
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
      });
      setForm({ name: '', category: 'restaurant', description: '', address: '', neighborhood: '', phone: '', website: '' });
      setShowForm(false);
      fetchBusinesses();
    } catch {}
    setCreating(false);
  };

  const renderStars = (rating: number | null) => {
    if (rating === null) return <span className="text-sand-400">No reviews yet</span>;
    const full = Math.round(rating);
    return (
      <span>
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={i < full ? 'text-amber-400' : 'text-sand-300'}>★</span>
        ))}
        <span className="text-sand-500 ml-1">({rating.toFixed(1)})</span>
      </span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-3 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-sand-800">Local Directory</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-xs px-3 py-1.5">
          + Add a Spot
        </button>
      </div>

      {/* Search & filter */}
      <div className="card p-3 flex gap-2 items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search businesses..."
          className="input flex-1 text-xs"
        />
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); fetchBusinesses(search, e.target.value); }}
          className="input text-xs w-32"
        >
          <option value="">All types</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
        <button onClick={handleSearch} className="btn-secondary text-xs px-3 py-1.5">Search</button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card p-4 space-y-3">
          <div className="card-header -mx-4 -mt-4 mb-3">Add a Local Spot</div>
          <div className="flex gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business name" className="input flex-1 text-sm" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input text-sm w-36">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this place about?" rows={2} className="input w-full text-sm resize-none" />
          <div className="flex gap-3">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="input flex-1 text-sm" />
            <input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} placeholder="Neighborhood" className="input flex-1 text-sm" />
          </div>
          <div className="flex gap-3">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="input flex-1 text-sm" />
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website" className="input flex-1 text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
            <button onClick={handleCreate} disabled={creating || !form.name.trim()} className="btn-primary text-xs px-4 py-1.5 disabled:opacity-40">
              {creating ? 'Adding...' : 'Add Spot'}
            </button>
          </div>
        </div>
      )}

      {/* Business list */}
      {loading ? (
        <div className="text-center py-8 text-sand-500">
          <div className="animate-pulse text-3xl mb-2">〰️</div>
          <p className="text-sm">Loading directory...</p>
        </div>
      ) : businesses.length === 0 ? (
        <div className="card p-8 text-center text-sand-500">
          <div className="text-3xl mb-2">🏪</div>
          <p className="text-sm">No businesses found.</p>
          <p className="text-xs mt-1">Know a great local spot? Add it!</p>
        </div>
      ) : (
        businesses.map((biz) => (
          <div key={biz.id} className="card p-4">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="font-bold text-sand-800 text-sm">{biz.name}</h2>
                <span className="text-[10px] font-bold text-vibe-500 uppercase">{biz.category}</span>
              </div>
              <div className="text-xs">{renderStars(biz.avg_rating)}</div>
            </div>
            {biz.description && <p className="text-xs text-sand-600 mb-2">{biz.description}</p>}
            <div className="flex flex-wrap gap-3 text-[11px] text-sand-500">
              {biz.address && <span>📍 {biz.address}</span>}
              {biz.neighborhood && <span>🏘️ {biz.neighborhood}</span>}
              {biz.phone && <span>📞 {biz.phone}</span>}
              {biz.website && <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="link">🌐 Website</a>}
              <span className="text-sand-400">{biz.review_count} review{biz.review_count !== 1 ? 's' : ''}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── Friends Page ────────────────────────────────────────────

interface FriendItem {
  id: string;
  display_name: string;
  username: string;
  avatar_key: string | null;
  neighborhood: string | null;
  last_seen_at: string | null;
}

interface PendingRequest {
  connection_id: string;
  id: string;
  display_name: string;
  username: string;
  avatar_key: string | null;
  requested_at: string;
}

export function FriendsPage() {
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [pending, setPending] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'friends' | 'pending'>('friends');
  const [search, setSearch] = useState('');
  const { sendHi } = useHiStore();
  const [hiSent, setHiSent] = useState<Set<string>>(new Set());

  useEffect(() => {
    Promise.all([
      api.get<FriendItem[]>('/connections?limit=50'),
      api.get<PendingRequest[]>('/connections/pending'),
    ])
      .then(([f, p]) => { setFriends(f); setPending(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAccept = async (connectionId: string) => {
    try {
      await api.post(`/connections/${connectionId}/accept`);
      setPending((prev) => prev.filter((p) => p.connection_id !== connectionId));
    } catch {}
  };

  const handleDecline = async (connectionId: string) => {
    try {
      await api.post(`/connections/${connectionId}/decline`);
      setPending((prev) => prev.filter((p) => p.connection_id !== connectionId));
    } catch {}
  };

  const handleHi = async (userId: string) => {
    try {
      await sendHi(userId);
      setHiSent((prev) => new Set(prev).add(userId));
    } catch {}
  };

  const isOnline = (lastSeen: string | null) => {
    if (!lastSeen) return false;
    return Date.now() - new Date(lastSeen + 'Z').getTime() < 5 * 60 * 1000;
  };

  const filtered = search
    ? friends.filter((f) => f.display_name.toLowerCase().includes(search.toLowerCase()) || f.username.toLowerCase().includes(search.toLowerCase()))
    : friends;

  return (
    <div className="max-w-3xl mx-auto px-3 py-4 space-y-3">
      <h1 className="text-lg font-bold text-sand-800">Friends</h1>

      {/* Tabs */}
      <div className="flex gap-0.5">
        <button
          onClick={() => setTab('friends')}
          className={`text-xs font-bold py-1.5 px-4 rounded-t-md transition-colors ${
            tab === 'friends' ? 'bg-white text-sand-800 border border-sand-300 border-b-white' : 'text-sand-500 hover:text-sand-700'
          }`}
        >
          All Friends ({friends.length})
        </button>
        <button
          onClick={() => setTab('pending')}
          className={`text-xs font-bold py-1.5 px-4 rounded-t-md transition-colors ${
            tab === 'pending' ? 'bg-white text-sand-800 border border-sand-300 border-b-white' : 'text-sand-500 hover:text-sand-700'
          }`}
        >
          Requests {pending.length > 0 && <span className="bg-vibe-500 text-white text-[9px] rounded-full px-1.5 ml-1">{pending.length}</span>}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sand-500">
          <div className="animate-pulse text-3xl mb-2">〰️</div>
          <p className="text-sm">Loading...</p>
        </div>
      ) : tab === 'friends' ? (
        <>
          {/* Search */}
          <div className="card p-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search friends..."
              className="input w-full text-xs"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-sand-500">
              <div className="text-3xl mb-2">👥</div>
              <p className="text-sm">{search ? 'No friends match that search' : 'No friends yet. Get out there!'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map((friend) => (
                <div key={friend.id} className="card p-3 flex items-center gap-3">
                  <Link to={`/profile/${friend.username}`} className="relative shrink-0">
                    <img src={mediaUrl(friend.avatar_key)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    {isOnline(friend.last_seen_at) && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/profile/${friend.username}`} className="link text-xs font-bold block truncate">
                      {friend.display_name}
                    </Link>
                    <p className="text-[10px] text-sand-500">@{friend.username}</p>
                    {friend.neighborhood && <p className="text-[10px] text-sand-400">📍 {friend.neighborhood}</p>}
                  </div>
                  {hiSent.has(friend.id) ? (
                    <span className="text-[10px] text-sand-400 shrink-0">👋</span>
                  ) : (
                    <button
                      onClick={() => handleHi(friend.id)}
                      className="text-[10px] font-bold text-amber-600 hover:text-amber-700 shrink-0 py-1 px-2 rounded hover:bg-amber-50 transition-colors"
                    >
                      👋 Hi
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Pending requests */
        pending.length === 0 ? (
          <div className="card p-8 text-center text-sand-500">
            <div className="text-3xl mb-2">✉️</div>
            <p className="text-sm">No pending friend requests</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((req) => (
              <div key={req.connection_id} className="card p-3 flex items-center gap-3">
                <Link to={`/profile/${req.username}`} className="shrink-0">
                  <img src={mediaUrl(req.avatar_key)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                </Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/profile/${req.username}`} className="link text-xs font-bold block truncate">
                    {req.display_name}
                  </Link>
                  <p className="text-[10px] text-sand-500">@{req.username} &middot; {timeAgo(req.requested_at)}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => handleAccept(req.connection_id)} className="btn-primary text-[11px] px-3 py-1">
                    Accept
                  </button>
                  <button onClick={() => handleDecline(req.connection_id)} className="btn-secondary text-[11px] px-3 py-1">
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── Notifications Page ──────────────────────────────────────

interface NotificationItem {
  id: string;
  type: string;
  actor_display_name: string | null;
  actor_username: string | null;
  actor_avatar_key: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: number;
  created_at: string;
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { markAllRead } = useNotifStore();

  useEffect(() => {
    api.get<NotificationItem[]>('/notifications?limit=50')
      .then(setNotifications)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleMarkAllRead = async () => {
    await markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
  };

  const notifMessage = (n: NotificationItem): string => {
    switch (n.type) {
      case 'like': return 'liked your post';
      case 'comment': return 'commented on your post';
      case 'connection_request': return 'sent you a friend request';
      case 'connection_accepted': return 'accepted your friend request';
      case 'event_invite': return 'invited you to an event';
      case 'message': return 'sent you a message';
      case 'hi': return 'said Hi! 👋';
      default: return 'did something';
    }
  };

  const notifLink = (n: NotificationItem): string => {
    if (n.type === 'connection_request' || n.type === 'connection_accepted' || n.type === 'hi') {
      return n.actor_username ? `/profile/${n.actor_username}` : '/friends';
    }
    if (n.type === 'message') return '/messages';
    if (n.type === 'like' || n.type === 'comment') return '/feed';
    if (n.type === 'event_invite') return '/events';
    return '/feed';
  };

  return (
    <div className="max-w-3xl mx-auto px-3 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-sand-800">Notifications</h1>
        {notifications.some((n) => !n.is_read) && (
          <button onClick={handleMarkAllRead} className="text-xs text-vibe-500 hover:text-vibe-600 font-bold">
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-sand-500">
          <div className="animate-pulse text-3xl mb-2">〰️</div>
          <p className="text-sm">Loading notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card p-8 text-center text-sand-500">
          <div className="text-3xl mb-2">🔔</div>
          <p className="text-sm">You're all caught up ∿</p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {notifications.map((notif) => (
            <Link
              key={notif.id}
              to={notifLink(notif)}
              className={`card flex items-center gap-3 p-3 hover:bg-sand-50 transition-colors ${
                !notif.is_read ? 'bg-vibe-50 border-vibe-200' : ''
              }`}
            >
              <img
                src={mediaUrl(notif.actor_avatar_key)}
                alt=""
                className="w-10 h-10 rounded-lg object-cover shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-sand-700">
                  <span className="font-bold">{notif.actor_display_name || 'Someone'}</span>{' '}
                  {notifMessage(notif)}
                </p>
                <span className="text-[10px] text-sand-400">{timeAgo(notif.created_at)}</span>
              </div>
              {!notif.is_read && (
                <span className="w-2 h-2 rounded-full bg-vibe-500 shrink-0" />
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
